import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '@/components/Header';
import { SearchFilters as FullSearchFilters } from '@/components/research/SearchFilters';
import { API_CONFIG } from '@/services/api';
import { useAppStore } from '@/stores/appStore';
import { useResearchStore } from '@/stores/researchStore';
import { searchLeads } from '@/services/research';
import scrappyFilterSpec from '@/config/scrappyFilterSpec.json';
import scrappyPromptV2 from '@/config/scrappy-perplexity-prompt-v2.txt?raw';
import {
  DEPARTMENT_OPTIONS,
  EMPLOYEE_SIZE_OPTIONS,
  INDUSTRY_OPTIONS,
  REVENUE_OPTIONS,
  SENIORITY_OPTIONS,
  type Lead,
  type SearchFilters,
} from '@/types/research';

/* ────────────────────────────────────────────
   Types
   ──────────────────────────────────────────── */

type ChatRole = 'user' | 'assistant';

type ChatMessage = {
  id: string;
  role: ChatRole;
  text: string;
};

type AiFilterSuggestion = Partial<
  Pick<
    SearchFilters,
    | 'names'
    | 'companies'
    | 'titles'
    | 'seniorities'
    | 'departments'
    | 'industries'
    | 'locations'
    | 'locationScope'
    | 'employeeSizes'
    | 'estimatedRevenues'
    | 'keywords'
    | 'skills'
    | 'skillsMode'
    | 'jobChangesType'
    | 'jobChangesTypeMode'
    | 'jobChangesDayRange'
  >
>;

type PerplexityResponse = {
  mode: 'filter' | 'discovery';
  thinking: string;
  filters: AiFilterSuggestion;
  summary: string;
  clarifications?: string[];
  refinements?: string[];
};

type ScrappyFilterSpec = {
  requiredCoreFilters?: string[];
  enums?: Record<string, string[]>;
};

/* ────────────────────────────────────────────
   Constants
   ──────────────────────────────────────────── */

const EMPTY_FILTERS: SearchFilters = {
  names: [],
  companies: [],
  titles: [],
  seniorities: [],
  departments: [],
  industries: [],
  locations: [],
  locationScope: 'both_or',
  locationRadiusMiles: 25,
  employeeSizes: [],
  estimatedRevenues: [],
  keywords: [],
  anyFormerCompanyMatch: true,
  overallSearch: true,
  contactSearchId: null,
  nextToken: null,
  page: 0,
};

const FILTER_SPEC = scrappyFilterSpec as ScrappyFilterSpec;

/* ────────────────────────────────────────────
   Validation sets
   ──────────────────────────────────────────── */

const VALID_SENIORITIES = new Set<string>(SENIORITY_OPTIONS);
const VALID_DEPARTMENTS = new Set<string>(DEPARTMENT_OPTIONS);
const VALID_INDUSTRIES = new Set<string>(INDUSTRY_OPTIONS);
const VALID_EMPLOYEE_SIZES = new Set<string>(EMPLOYEE_SIZE_OPTIONS);
const VALID_REVENUES = new Set<string>(REVENUE_OPTIONS);
const VALID_LOCATION_SCOPES: Array<NonNullable<SearchFilters['locationScope']>> = ['both_or', 'both_and', 'company', 'contact'];
const VALID_JOB_CHANGE_TYPES = new Set<string>(['Promotion', 'New Hire']);
const VALID_JOB_CHANGE_DAY_RANGES = new Set<string>(['Last 60 Days', 'Last 90 Days', 'Last 180 Days', 'Last 365 Days']);

/* ────────────────────────────────────────────
   Pure helpers
   ──────────────────────────────────────────── */

function unique(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values) {
    const key = value.trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(value.trim());
  }
  return out;
}

function hasCoreFilters(filters: SearchFilters): boolean {
  const core = FILTER_SPEC.requiredCoreFilters || [];
  const flat = filters as unknown as Record<string, unknown>;
  const isPresent = (field: string): boolean => {
    const value = flat[field];
    if (Array.isArray(value)) return value.length > 0;
    return Boolean(value);
  };
  if (core.length > 0) return core.some(isPresent);
  return (
    filters.names.length > 0 ||
    filters.companies.length > 0 ||
    filters.titles.length > 0 ||
    filters.seniorities.length > 0 ||
    filters.departments.length > 0 ||
    filters.industries.length > 0 ||
    filters.locations.length > 0 ||
    filters.employeeSizes.length > 0 ||
    filters.estimatedRevenues.length > 0 ||
    filters.keywords.length > 0
  );
}

/* ────────────────────────────────────────────
   Canonicalization / normalization
   ──────────────────────────────────────────── */

function canonicalizeEmployeeSize(raw: string): string | null {
  const normalized = String(raw || '').toLowerCase().replace(/\s+/g, '');
  for (const option of EMPLOYEE_SIZE_OPTIONS) {
    const key = option.toLowerCase().replace(/\s+/g, '');
    if (key === normalized) return option;
  }
  if (normalized.includes('51-200') || normalized.includes('51–200')) return '51 - 200';
  if (normalized.includes('201-500') || normalized.includes('201–500')) return '201 - 500';
  if (normalized.includes('11-50') || normalized.includes('11–50')) return '11 - 50';
  if (normalized.includes('2-10') || normalized.includes('2–10')) return '2 - 10';
  if (normalized.includes('501-1000') || normalized.includes('501–1000') || normalized.includes('501-1,000')) return '501 - 1,000';
  if (normalized.includes('1001-5000') || normalized.includes('1,001-5,000')) return '1,001 - 5,000';
  if (normalized.includes('5001-10000') || normalized.includes('5,001-10,000')) return '5,001 - 10,000';
  if (normalized.includes('10001') || normalized.includes('10,001')) return '10,001+';
  if (normalized === 'self-employed' || normalized === 'selfemployed' || normalized.includes('0-1')) return '0 - 1 (Self-employed)';
  return null;
}

function canonicalizeIndustry(raw: string): string | null {
  const lower = String(raw || '').toLowerCase().trim();
  if (!lower) return null;
  const exact = INDUSTRY_OPTIONS.find((option) => option.toLowerCase() === lower);
  if (exact) return exact;
  if (lower.includes('saas') || lower.includes('software') || (lower.includes('tech') && !lower.includes('biotech'))) return 'Software & Information Technology';
  if (lower.includes('landscap') || lower.includes('plumb') || lower.includes('electric') || lower.includes('roof') || lower.includes('hvac') || lower.includes('contractor') || lower.includes('home service') || lower.includes('construction') || lower.includes('real estate')) return 'Real Estate & Construction';
  if (lower.includes('restaurant') || lower.includes('food service') || lower.includes('dining')) return 'Restaurants';
  if (lower.includes('food') || lower.includes('beverage') || lower.includes('ice cream') || lower.includes('bakery') || lower.includes('cafe') || lower.includes('coffee')) return 'Food & Beverage';
  if (lower.includes('healthcare') || lower.includes('medical') || lower.includes('pharma')) return 'Healthcare & Pharmaceuticals';
  if (lower.includes('finance') || lower.includes('banking') || lower.includes('insurance')) return 'Financial Services & Insurance';
  if (lower.includes('retail') || lower.includes('consumer goods') || lower.includes('ecommerce') || lower.includes('e-commerce')) return 'Consumer Goods & Retail';
  if (lower.includes('hotel') || lower.includes('hospitality') || lower.includes('tourism') || lower.includes('travel')) return 'Hospitality & Tourism';
  if (lower.includes('education') || lower.includes('edtech') || lower.includes('university') || lower.includes('school')) return 'Education';
  if (lower.includes('media') || lower.includes('entertainment') || lower.includes('publishing')) return 'Media & Entertainment';
  if (lower.includes('manufactur')) return 'Manufacturing & Industrial';
  if (lower.includes('logistics') || lower.includes('transport') || lower.includes('shipping') || lower.includes('supply chain')) return 'Transportation & Logistics';
  if (lower.includes('energy') || lower.includes('oil') || lower.includes('gas') || lower.includes('renewable') || lower.includes('solar')) return 'Energy & Utilities';
  if (lower.includes('telecom') || lower.includes('communication')) return 'Telecommunications';
  if (lower.includes('government') || lower.includes('public sector') || lower.includes('federal')) return 'Government & Public Sector';
  if (lower.includes('nonprofit') || lower.includes('non-profit') || lower.includes('ngo') || lower.includes('charity')) return 'Nonprofits & NGOs';
  if (lower.includes('legal') || lower.includes('law firm') || lower.includes('attorney')) return 'Legal Services';
  if (lower.includes('consulting') || lower.includes('professional service') || lower.includes('advisory')) return 'Business Services & Consulting';
  if (lower.includes('marketing') || lower.includes('advertising') || lower.includes('agency') || lower.includes('creative')) return 'Marketing & Advertising';
  if (lower.includes('automotive') || lower.includes('dealership') || lower.includes('car')) return 'Automotive';
  if (lower.includes('aerospace') || lower.includes('defense') || lower.includes('military')) return 'Aerospace & Defense';
  if (lower.includes('agri') || lower.includes('farming') || lower.includes('agriculture')) return 'Agriculture & Farming';
  return null;
}

function canonicalizeScope(raw: string): SearchFilters['locationScope'] {
  const lower = String(raw || '').toLowerCase();
  if (lower.includes('company')) return 'company';
  if (lower.includes('contact')) return 'contact';
  if (lower.includes('and')) return 'both_and';
  return 'both_or';
}

function canonicalizeRevenue(raw: string): string | null {
  const lower = String(raw || '').toLowerCase().trim();
  return REVENUE_OPTIONS.find((option) => option.toLowerCase() === lower) || null;
}

function canonicalizeSuggestion(suggestion: AiFilterSuggestion): AiFilterSuggestion {
  const dedupe = (values: string[] = []) => unique(values.map((v) => String(v ?? '').trim()).filter(Boolean));
  const toAllowed = (raw: string, allowed: string[]): string | null => {
    const lower = String(raw || '').toLowerCase();
    return allowed.find((option) => option.toLowerCase() === lower) || null;
  };

  const next: AiFilterSuggestion = { ...suggestion };

  if (next.names) next.names = dedupe(next.names);
  if (next.companies) next.companies = dedupe(next.companies);
  if (next.titles) next.titles = dedupe(next.titles);
  if (next.keywords) next.keywords = dedupe(next.keywords);
  if (next.locations) next.locations = dedupe(next.locations).slice(0, 5);
  if (next.skills) next.skills = dedupe(next.skills);

  if (next.seniorities) {
    next.seniorities = next.seniorities
      .map((v) => toAllowed(v, SENIORITY_OPTIONS) || v)
      .filter((v) => VALID_SENIORITIES.has(v));
  }
  if (next.departments) {
    next.departments = next.departments
      .map((v) => toAllowed(v, DEPARTMENT_OPTIONS) || v)
      .filter((v) => VALID_DEPARTMENTS.has(v))
      .filter((v) => v !== 'Other');
  }
  if (next.employeeSizes) {
    next.employeeSizes = next.employeeSizes
      .map((v) => canonicalizeEmployeeSize(v))
      .filter((v): v is string => Boolean(v));
  }
  if (next.estimatedRevenues) {
    next.estimatedRevenues = next.estimatedRevenues
      .map((v) => canonicalizeRevenue(v))
      .filter((v): v is string => Boolean(v));
  }
  if (next.industries) {
    next.industries = unique(
      next.industries
        .map((v) => canonicalizeIndustry(v))
        .filter((v): v is string => Boolean(v))
    ).slice(0, 2);
  }
  if (next.locationScope) next.locationScope = canonicalizeScope(next.locationScope);
  if (next.jobChangesType) {
    next.jobChangesType = next.jobChangesType.filter((v) => VALID_JOB_CHANGE_TYPES.has(v));
  }
  if (next.jobChangesDayRange) {
    next.jobChangesDayRange = next.jobChangesDayRange.filter((v) => VALID_JOB_CHANGE_DAY_RANGES.has(v));
  }
  if (next.jobChangesTypeMode && next.jobChangesTypeMode !== 'and') next.jobChangesTypeMode = 'or';
  if (next.skillsMode && next.skillsMode !== 'and') next.skillsMode = 'or';

  return next;
}

function normalizeToAllowedFilters(input: SearchFilters): SearchFilters {
  const dedupe = (values: string[] = []) => unique(values.map((v) => String(v ?? '').trim()).filter(Boolean));
  const onlyAllowed = (values: string[], allowed: Set<string>) => dedupe(values).filter((v) => allowed.has(v));
  const scopeCandidate = (input.locationScope || 'both_or') as NonNullable<SearchFilters['locationScope']>;
  const scope = VALID_LOCATION_SCOPES.includes(scopeCandidate) ? scopeCandidate : 'both_or';

  return {
    ...input,
    names: dedupe(input.names),
    companies: dedupe(input.companies),
    titles: dedupe(input.titles),
    seniorities: onlyAllowed(input.seniorities, VALID_SENIORITIES),
    departments: onlyAllowed(input.departments, VALID_DEPARTMENTS).filter((v) => v !== 'Other'),
    industries: onlyAllowed(input.industries, VALID_INDUSTRIES).slice(0, 2),
    locations: dedupe(input.locations).slice(0, 5),
    locationScope: scope,
    employeeSizes: onlyAllowed(input.employeeSizes, VALID_EMPLOYEE_SIZES),
    estimatedRevenues: onlyAllowed(input.estimatedRevenues, VALID_REVENUES),
    keywords: dedupe(input.keywords),
    skills: dedupe((input.skills || []) as string[]),
    skillsMode: input.skillsMode === 'and' ? 'and' : 'or',
    jobChangesType: onlyAllowed((input.jobChangesType || []) as string[], VALID_JOB_CHANGE_TYPES),
    jobChangesTypeMode: input.jobChangesTypeMode === 'and' ? 'and' : 'or',
    jobChangesDayRange: onlyAllowed((input.jobChangesDayRange || []) as string[], VALID_JOB_CHANGE_DAY_RANGES),
  };
}

/* ────────────────────────────────────────────
   JSON extraction from AI response
   ──────────────────────────────────────────── */

function extractFirstJsonObject(raw: string): Record<string, unknown> | null {
  const text = String(raw || '').trim();
  if (!text) return null;
  const fenced = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
  const candidateList = [fenced?.[1] ?? '', text];
  for (const candidate of candidateList) {
    const trimmed = candidate.trim();
    if (!trimmed) continue;
    try {
      const parsed = JSON.parse(trimmed) as Record<string, unknown>;
      if (parsed && typeof parsed === 'object') return parsed;
    } catch {
      const start = trimmed.indexOf('{');
      const end = trimmed.lastIndexOf('}');
      if (start >= 0 && end > start) {
        const slice = trimmed.slice(start, end + 1);
        try {
          const parsed = JSON.parse(slice) as Record<string, unknown>;
          if (parsed && typeof parsed === 'object') return parsed;
        } catch {
          // ignore
        }
      }
    }
  }
  return null;
}

function extractPerplexityResponse(raw: string): PerplexityResponse | null {
  const parsed = extractFirstJsonObject(raw);
  if (!parsed) return null;
  const rawMode = String((parsed as { mode?: unknown }).mode || '');
  const mode: PerplexityResponse['mode'] = rawMode === 'discovery' ? 'discovery' : 'filter';
  return {
    mode,
    thinking: String((parsed as { thinking?: unknown }).thinking || ''),
    filters: ((parsed as { filters?: unknown }).filters || {}) as AiFilterSuggestion,
    summary: String((parsed as { summary?: unknown }).summary || ''),
    clarifications: Array.isArray((parsed as { clarifications?: unknown }).clarifications)
      ? (parsed as { clarifications: string[] }).clarifications
      : undefined,
    refinements: Array.isArray((parsed as { refinements?: unknown }).refinements)
      ? (parsed as { refinements: string[] }).refinements
      : undefined,
  };
}

/* ────────────────────────────────────────────
   Display helpers
   ──────────────────────────────────────────── */

function summarizeFilters(filters: SearchFilters): string {
  const parts: string[] = [];
  if (filters.titles.length) parts.push(`titles: ${filters.titles.join(', ')}`);
  if (filters.seniorities.length) parts.push(`seniority: ${filters.seniorities.join(', ')}`);
  if (filters.departments.length) parts.push(`departments: ${filters.departments.join(', ')}`);
  if (filters.industries.length) parts.push(`industries: ${filters.industries.join(', ')}`);
  if (filters.locations.length) parts.push(`locations: ${filters.locations.join(', ')} (${filters.locationScope || 'both_or'})`);
  if (filters.employeeSizes.length) parts.push(`employee size: ${filters.employeeSizes.join(', ')}`);
  if (filters.estimatedRevenues.length) parts.push(`revenue: ${filters.estimatedRevenues.join(', ')}`);
  if (filters.keywords.length) parts.push(`keywords: ${filters.keywords.join(', ')}`);
  if (!parts.length) return 'No structured filters extracted yet.';
  return parts.join(' · ');
}

/* ────────────────────────────────────────────
   The Perplexity system prompt
   ──────────────────────────────────────────── */

function buildSystemPrompt(currentFilters: SearchFilters): string {
  return `${scrappyPromptV2.trim()}\n\n## Current filter state\n\n${JSON.stringify(currentFilters, null, 2)}`;
}

/* ────────────────────────────────────────────
   Perplexity API call
   ──────────────────────────────────────────── */

async function callGuideApi(
  intent: string,
  conversationContext: string[],
  currentFilters: SearchFilters
): Promise<PerplexityResponse | null> {
  const guideApi = (API_CONFIG as { GUIDE_API?: string }).GUIDE_API;
  if (!guideApi) return null;

  const payload = {
    prompt: [
      buildSystemPrompt(currentFilters),
      '',
      '## Conversation so far',
      ...conversationContext.slice(-8),
      '',
      '## Current user message',
      intent,
    ].join('\n'),
    page: 'scrappy',
    path: '/scrappy',
    context: { searchFilters: currentFilters },
  };

  const response = await fetch(guideApi, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) return null;
  const result = (await response.json()) as { reply?: string };
  const rawReply = String(result.reply || '');
  return extractPerplexityResponse(rawReply);
}

/* ────────────────────────────────────────────
   Merge logic
   ──────────────────────────────────────────── */

function mergeSuggestionIntoFilters(base: SearchFilters, suggestion: AiFilterSuggestion): SearchFilters {
  return normalizeToAllowedFilters({
    ...base,
    ...suggestion,
    names: unique([...(base.names || []), ...((suggestion.names || []) as string[])]),
    companies: unique([...(base.companies || []), ...((suggestion.companies || []) as string[])]),
    titles: unique([...(base.titles || []), ...((suggestion.titles || []) as string[])]),
    seniorities: unique([...(base.seniorities || []), ...((suggestion.seniorities || []) as string[])]),
    departments: unique([...(base.departments || []), ...((suggestion.departments || []) as string[])]),
    industries: unique([...(base.industries || []), ...((suggestion.industries || []) as string[])]),
    locations: unique([...(base.locations || []), ...((suggestion.locations || []) as string[])]),
    employeeSizes: unique([...(base.employeeSizes || []), ...((suggestion.employeeSizes || []) as string[])]),
    estimatedRevenues: unique([...(base.estimatedRevenues || []), ...((suggestion.estimatedRevenues || []) as string[])]),
    keywords: unique([...(base.keywords || []), ...((suggestion.keywords || []) as string[])]),
    skills: unique([...(base.skills || []), ...((suggestion.skills || []) as string[])]),
  });
}

/* ────────────────────────────────────────────
   Component
   ──────────────────────────────────────────── */

export function Scrappy() {
  const navigate = useNavigate();
  const userEmail = useAppStore((state) => state.userEmail);
  const {
    resetFilters: resetResearchFilters,
    setFilters: setResearchFilters,
    setPendingSearch,
    setPerPage: setResearchPerPage,
  } = useResearchStore();

  const chatEndRef = useRef<HTMLDivElement>(null);

  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'intro',
      role: 'assistant',
      text: "Hey — I'm Scrappy. I help you find the right people to reach out to.\n\nIf you already know your target, describe them and I'll build the search. Something like \"ice cream shop owners in Arizona\" or \"VP of Sales at mid-market SaaS companies.\"\n\nIf you're not sure who to target yet, just tell me what you sell and I'll help you figure out your ideal customer profile.",
    },
  ]);
  const [input, setInput] = useState('');
  const [workingFilters, setWorkingFilters] = useState<SearchFilters>({ ...EMPTY_FILTERS });
  const [sessionFilters, setSessionFilters] = useState<SearchFilters>({ ...EMPTY_FILTERS });
  const [leads, setLeads] = useState<Lead[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [perPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [aiSuggesting, setAiSuggesting] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const interpretedSummary = useMemo(() => summarizeFilters(workingFilters), [workingFilters]);

  // Sync working filters to research store
  useEffect(() => {
    setResearchFilters(workingFilters);
  }, [workingFilters, setResearchFilters]);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const pushMessage = (role: ChatRole, text: string) => {
    setMessages((prev) => [
      ...prev,
      { id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, role, text },
    ]);
  };

  const getConversationContext = (): string[] => {
    return messages.slice(-10).map((m) => `${m.role === 'user' ? 'User' : 'Scrappy'}: ${m.text}`);
  };

  /* ── Search execution ── */

  const runSearch = async (filters: SearchFilters, nextPage: number, resetCursor: boolean) => {
    setLoading(true);
    setError('');
    try {
      const active = resetCursor ? { ...filters, contactSearchId: null, nextToken: null } : filters;
      const result = await searchLeads(active, nextPage, perPage, { userEmail });
      setLeads(result.contacts);
      setTotal(result.total);
      setPage(nextPage);
      const next = { ...active, contactSearchId: result.contactSearchId, nextToken: result.nextToken };
      setSessionFilters(next);
      setWorkingFilters(next);
      return result;
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Search failed';
      setError(message);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  /* ── Main submit handler ── */

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const prompt = input.trim();
    if (!prompt || loading) return;
    setInput('');
    setError('');
    pushMessage('user', prompt);

    setLoading(true);
    try {
      const aiResponse = await callGuideApi(prompt, getConversationContext(), workingFilters);

      if (!aiResponse) {
        pushMessage('assistant', "I couldn't process that — the AI service didn't return a usable response. Try rephrasing, or use the filter panel on the right to build manually.");
        setLoading(false);
        return;
      }

      // ── Discovery mode: Perplexity needs more info ──
      if (aiResponse.mode === 'discovery') {
        const parts: string[] = [];
        if (aiResponse.thinking) parts.push(aiResponse.thinking);
        if (aiResponse.clarifications && aiResponse.clarifications.length > 0) {
          parts.push(aiResponse.clarifications.join('\n'));
        }
        pushMessage('assistant', parts.join('\n\n') || "Tell me more about what you sell or who you're trying to reach.");
        setLoading(false);
        return;
      }

      // ── Filter mode: build and search ──
      const canonicalized = canonicalizeSuggestion(aiResponse.filters);
      const merged = mergeSuggestionIntoFilters(workingFilters, canonicalized);

      const hasClarifications = aiResponse.clarifications && aiResponse.clarifications.length > 0;
      const hasFilters = hasCoreFilters(merged);

      // Filter mode but still not enough for a search
      if (!hasFilters) {
        const parts: string[] = [];
        if (aiResponse.thinking) parts.push(aiResponse.thinking);
        if (hasClarifications) {
          parts.push(aiResponse.clarifications!.join('\n'));
        } else {
          parts.push("I need a bit more to build a useful search. What industry are they in, what's their role, or where are they located?");
        }
        pushMessage('assistant', parts.join('\n\n'));
        setLoading(false);
        return;
      }

      // We have filters — apply and search
      setWorkingFilters(merged);
      setSessionFilters(merged);

      const parts: string[] = [];
      if (aiResponse.thinking) parts.push(aiResponse.thinking);
      if (aiResponse.summary) parts.push(aiResponse.summary);

      try {
        const result = await runSearch(merged, 0, true);
        parts.push(`\n${result.total.toLocaleString()} results found.`);
        if (aiResponse.refinements && aiResponse.refinements.length > 0) {
          parts.push(`\nTo narrow further: ${aiResponse.refinements.join(' ')}`);
        }
        if (hasClarifications) {
          parts.push(`\n${aiResponse.clarifications!.join(' ')}`);
        }
      } catch {
        parts.push('\nSearch execution failed — filters are set, you can retry or adjust in the filter panel.');
      }

      pushMessage('assistant', parts.join('\n'));
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Something went wrong';
      setError(message);
      pushMessage('assistant', `Something went wrong: ${message}`);
    } finally {
      setLoading(false);
    }
  };

  /* ── AI suggest from current filters ── */

  const requestAiSuggestion = async () => {
    if (aiSuggesting) return;
    setAiSuggesting(true);
    setError('');
    try {
      const lastIntent = messages.filter((m) => m.role === 'user').slice(-1)[0]?.text || 'Suggest improvements to the current filters.';
      const aiResponse = await callGuideApi(
        `Given my current filters, suggest improvements or additions. My last request was: "${lastIntent}"`,
        getConversationContext(),
        workingFilters
      );
      if (!aiResponse) throw new Error('AI suggestion returned no response.');
      const canonicalized = canonicalizeSuggestion(aiResponse.filters);
      const merged = mergeSuggestionIntoFilters(workingFilters, canonicalized);
      setWorkingFilters(merged);
      setSessionFilters(merged);
      const msg = aiResponse.thinking || aiResponse.summary || 'Applied AI suggestions to your filters.';
      pushMessage('assistant', msg);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'AI suggestion failed');
      pushMessage('assistant', e instanceof Error ? e.message : 'AI suggestion failed.');
    } finally {
      setAiSuggesting(false);
    }
  };

  /* ── Navigation / reset ── */

  const openInFullSearch = () => {
    const handoff = { ...sessionFilters };
    resetResearchFilters();
    setResearchPerPage(perPage);
    setResearchFilters(handoff);
    setPendingSearch({ filters: handoff, page, perPage });
    navigate('/lead-search');
  };

  const resetSession = () => {
    setWorkingFilters({ ...EMPTY_FILTERS });
    setSessionFilters({ ...EMPTY_FILTERS });
    setLeads([]);
    setTotal(0);
    setPage(0);
    setError('');
    setShowFilters(false);
    pushMessage('assistant', "Clean slate. Tell me who you're going after, or tell me what you sell and we'll figure it out together.");
  };

  /* ── Render ── */

  return (
    <div className="min-h-screen bg-dark-bg text-white">
      <Header />
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 grid grid-cols-1 xl:grid-cols-5 gap-6">

        {/* ── Chat Panel ── */}
        <section className="xl:col-span-2 bg-card-dark border border-border-dark rounded-xl p-4 h-[78vh] flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-lg font-semibold">Scrappy</h1>
            <button
              onClick={resetSession}
              className="text-xs px-3 py-1 rounded border border-gray-700 hover:bg-gray-800"
            >
              New Search
            </button>
          </div>

          <div className="flex-1 overflow-y-auto space-y-3 pr-1">
            {messages.map((message) => (
              <div key={message.id} className={message.role === 'assistant' ? 'text-sm text-gray-200' : 'text-sm text-white'}>
                <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">
                  {message.role === 'assistant' ? 'Scrappy' : 'You'}
                </div>
                <div className="bg-black/30 border border-gray-800 rounded-lg px-3 py-2 whitespace-pre-wrap">
                  {message.text}
                </div>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>

          <form onSubmit={(event) => void handleSubmit(event)} className="mt-3 flex gap-2">
            <input
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="Describe who you're looking for..."
              className="flex-1 bg-black/40 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-500"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="px-4 py-2 rounded bg-red-600 hover:bg-red-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-sm font-semibold"
            >
              {loading ? 'Thinking…' : 'Send'}
            </button>
          </form>
        </section>

        {/* ── Results Panel ── */}
        <section className="xl:col-span-3 bg-card-dark border border-border-dark rounded-xl p-4 h-[78vh] flex flex-col">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold">Results</h2>
              {hasCoreFilters(workingFilters) && (
                <p className="text-xs text-gray-400 mt-1 truncate">{interpretedSummary}</p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="px-3 py-1.5 text-xs rounded border border-gray-700 hover:bg-gray-800"
              >
                {showFilters ? 'Hide Filters' : 'Edit Filters'}
              </button>
              <button
                onClick={openInFullSearch}
                disabled={!leads.length}
                className={`px-3 py-1.5 text-xs rounded font-semibold transition-all ${
                  leads.length
                    ? 'bg-emerald-500 hover:bg-emerald-400 text-black ring-2 ring-emerald-300 shadow-lg shadow-emerald-900/40 animate-pulse'
                    : 'bg-gray-800 hover:bg-gray-700 text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed'
                }`}
              >
                {leads.length ? 'Open Full Search For Research' : 'Open Full Search'}
              </button>
            </div>
          </div>

          {/* Collapsible filter panel */}
          {showFilters && (
            <div className="mb-3 border border-gray-800 rounded-lg p-3 bg-black/20 space-y-3">
              <FullSearchFilters
                onSearch={(nextFilters) => {
                  const active = (nextFilters ?? workingFilters) as SearchFilters;
                  setWorkingFilters(active);
                  setSessionFilters(active);
                  void runSearch(active, 0, true);
                }}
              />
              <div className="flex gap-2">
                <button
                  onClick={() => void requestAiSuggestion()}
                  disabled={aiSuggesting}
                  className="px-3 py-1.5 text-xs rounded border border-gray-700 hover:bg-gray-800 disabled:opacity-50"
                >
                  {aiSuggesting ? 'Suggesting…' : 'AI Suggest'}
                </button>
              </div>
            </div>
          )}

          {error && <p className="text-sm text-red-300 mb-3">{error}</p>}

          {/* Results table */}
          <div className="flex-1 overflow-auto border border-gray-800 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-black/40 text-gray-300 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-left px-3 py-2">Title</th>
                  <th className="text-left px-3 py-2">Company</th>
                  <th className="text-left px-3 py-2">Location</th>
                  <th className="text-left px-3 py-2">Researched</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr key={lead.searchResultId} className="border-t border-gray-800 hover:bg-white/[0.02]">
                    <td className="px-3 py-2">
                      {lead.name || `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || '-'}
                    </td>
                    <td className="px-3 py-2">{lead.title || '-'}</td>
                    <td className="px-3 py-2">
                      {lead.companyName || (typeof lead.company === 'string' ? lead.company : lead.company?.name) || '-'}
                    </td>
                    <td className="px-3 py-2">{lead.location || lead.city || lead.state || '-'}</td>
                    <td className="px-3 py-2">{lead.researchedData ? 'Yes' : 'No'}</td>
                  </tr>
                ))}
                {!leads.length && (
                  <tr>
                    <td colSpan={5} className="px-3 py-8 text-center text-gray-500">
                      {loading ? 'Searching…' : 'No results yet. Describe your target in the chat to get started.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-3 flex items-center justify-between text-sm">
            <div className="text-gray-400">
              {total > 0
                ? `${total.toLocaleString()} total · page ${page + 1} of ${totalPages}`
                : ''}
            </div>
            {total > 0 && (
              <div className="flex gap-2">
                <button
                  disabled={loading || page <= 0}
                  onClick={() => void runSearch(sessionFilters, page - 1, false)}
                  className="px-3 py-1.5 rounded border border-gray-700 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Prev
                </button>
                <button
                  disabled={loading || page + 1 >= totalPages}
                  onClick={() => void runSearch(sessionFilters, page + 1, false)}
                  className="px-3 py-1.5 rounded border border-gray-700 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
